import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./AttendeeDetailsComponent.css";
import SponsorService from "../../core/service/SponserService";

const AttendeeDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sponsorService = new SponsorService();

  const [attendees, setAttendees] = useState([]);
  const [sponsorData, setSponsorData] = useState(null);
  const [eventToken, setEventToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // FIX: Get token from state first, then fallback to localStorage
    const stateToken = location.state?.eventToken;
    const localToken = localStorage.getItem("eventToken");
    const token = stateToken || localToken;

    const passes = location.state?.noOfPasses || 1;

    // Get sponsor data from state
    const sponsorDataFromState = location.state?.sponsorData;
    if (sponsorDataFromState) {
      setSponsorData(sponsorDataFromState);
    } else {
      try {
        const storedSponsorData = sessionStorage.getItem("sponsorData");
        if (storedSponsorData) {
          setSponsorData(JSON.parse(storedSponsorData));
        }
      } catch (error) {
        toast.error("Failed to load sponsor data");
      }
    }

    if (token) {
      setEventToken(token.trim());
    } else {
      toast.error("Authentication failed. Please go back and try again.");
    }

    // Create the initial attendees based on the number of passes
    const initialAttendees = Array.from({ length: passes }, () => ({
      name: "",
      email: "",
      phone: "",
      designation: "",
    }));

    setAttendees(initialAttendees);
  }, [location.state]);

  const handleInputChange = (index, field, value) => {
    const updatedAttendees = [...attendees];
    updatedAttendees[index][field] = value;
    setAttendees(updatedAttendees);
  };

  const validateAttendees = () => {
    for (let i = 0; i < attendees.length; i++) {
      const attendee = attendees[i];
      if (!attendee.name.trim()) {
        toast.error(`Please enter name for Attendee ${i + 1}`);
        return false;
      }
      if (!attendee.email.trim()) {
        toast.error(`Please enter email for Attendee ${i + 1}`);
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(attendee.email)) {
        toast.error(`Please enter a valid email for Attendee ${i + 1}`);
        return false;
      }
      if (!attendee.phone.trim()) {
        toast.error(`Please enter phone number for Attendee ${i + 1}`);
        return false;
      }
      const phoneDigits = attendee.phone.replace(/\D/g, "");
      if (phoneDigits.length !== 10) {
        toast.error(
          `Please enter a valid 10-digit phone number for Attendee ${i + 1}`
        );
        return false;
      }
      // Add designation validation
      if (!attendee.designation.trim()) {
        toast.error(`Please enter designation for Attendee ${i + 1}`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAttendees()) return;
    if (!sponsorData) {
      toast.error("Sponsor data is missing. Please go back and try again.");
      return;
    }
    if (!eventToken) {
      toast.error("Event token is missing. Please go back and try again.");
      return;
    }

    setIsLoading(true);

    try {
      // Save attendees
      const response = await sponsorService.saveAttendees(
        sponsorData.sponsorId,
        attendees,
        eventToken
      );

      if (response.data.responseCode === 201) {
        toast.success("Attendees saved successfully!");
        navigate("/");
      } else {
        toast.error(
          response.data.responseMessage || "Failed to save attendees"
        );
      }
    } catch (error) {
      console.error("Error saving attendees:", error);
      if (error.response?.data?.responseMessage) {
        toast.error(error.response.data.responseMessage);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("An unknown error occurred while saving attendees");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    if (location.state) {
      // Extract event details from state (excluding sponsorData)
      const { sponsorData, ...eventDetails } = location.state;
      navigate("/sponsor-registration", { state: eventDetails });
    } else {
      navigate("/sponsor-registration");
    }
  };

  return (
    <div className="attendee-details-page">
      <div className="attendee-details-container">
        <div className="header-section">
          <h1 className="register-title">Register Now</h1>
          <button className="go-back-btn" onClick={handleGoBack}>
            ← Go Back
          </button>
          <p className="instruction-text">
            Please fill the details of the attendees to generate the passes
          </p>
        </div>

        <form className="attendee-form" onSubmit={handleSubmit}>
          {attendees.map((attendee, index) => (
            <div key={index} className="attendee-section">
              <h2 className="attendee-title">Attendee {index + 1}</h2>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Name *"
                  className="form-input"
                  value={attendee.name}
                  onChange={(e) =>
                    handleInputChange(index, "name", e.target.value)
                  }
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  placeholder="Email *"
                  className="form-input"
                  value={attendee.email}
                  onChange={(e) =>
                    handleInputChange(index, "email", e.target.value)
                  }
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  placeholder="Phone Number *"
                  className="form-input"
                  value={attendee.phone}
                  onChange={(e) =>
                    handleInputChange(index, "phone", e.target.value)
                  }
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Designation *"
                  className="form-input"
                  value={attendee.designation}
                  onChange={(e) =>
                    handleInputChange(index, "designation", e.target.value)
                  }
                  required
                />
              </div>
            </div>
          ))}

          <button type="submit" className="form-submit" disabled={isLoading}>
            {isLoading ? (
              <span>
                Saving...
                <span className="button-spinner"></span>
              </span>
            ) : (
              "Submit"
            )}
          </button>
        </form>
      </div>

      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        style={{ fontSize: "14px" }}
        toastStyle={{
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        }}
      />
    </div>
  );
};

export default AttendeeDetails;
